import './SingleSkuConfigWizard.less';

import {
  Affix,
  Divider,
  Modal,
  PageHeader,
  Progress,
  Steps,
  Tooltip,
} from 'antd';
import Text from 'antd/lib/typography/Text';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  ArrowLeftIcon,
  DoneCircleIcon,
  UncheckedCircleIcon,
} from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxSpin } from 'components/RevxSpin';
import { Headline } from 'components/RevxTypography';
import { PartialSKU, SKUPart, StepActionTypeEnum } from 'domains/skuDomain';
import { useSkuSvcRole } from 'hooks/useSkuSvcRole';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import skuService from 'services/skuService';
import {
  ADMIN_DETAIL_AFFIX_TOP,
  SKU_TYPE_NAME_PACKAGING,
} from 'utils/constants';
import SingleWizardStep from './SingleWizardStep';
import { withMeasurementUnitScope } from 'hooks/useMeasurementUnitScope';
import { RevxUnitSelect } from 'components/RevxUnitSelect';

const { Step } = Steps;
type Props = {
  onCancel: () => void;
  sku: PartialSKU;
  topSku?: PartialSKU;
  skuPart?: SKUPart;
};

const { confirm } = Modal;

const SingleSkuConfigWizard = ({ onCancel, sku, topSku, skuPart }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const singleStep = useAppSelector((state) => state.sku.singleStep);
  const revxSpinloading = useAppSelector((state) => state.sku.revxSpinloading);
  const [loading, setLoading] = useState(false);
  const stepAction = useAppSelector((state) => state.sku.stepAction);
  const stepActionPayload = useAppSelector(
    (state) => state.sku.stepActionPayload
  );
  const svcRole = useSkuSvcRole(sku);

  const totalStep = useMemo(() => {
    let returnData = 7;
    if (svcRole.isJobOption) {
      returnData++;
    }
    if (svcRole.isRequiredSvc) {
      returnData++;
    }
    if (svcRole.isAdditionalJobOption) {
      returnData++;
    }
    if (svcRole.isOptionalSvc) {
      returnData++;
    }
    return returnData;
  }, [svcRole]);

  const stepType: StepActionTypeEnum[] = useMemo(() => {
    let returnData = [
      StepActionTypeEnum.STEP1_QUANTITIES,
      StepActionTypeEnum.STEP2_SIZES,
      StepActionTypeEnum.STEP3_PAPER,
      StepActionTypeEnum.STEP4_PRESSES,
      StepActionTypeEnum.STEP5_UPLOADFILES,
      StepActionTypeEnum.STEP6_INKFINISHES,
    ];
    if (svcRole.isJobOption) {
      returnData.push(StepActionTypeEnum.STEP7_JOBOPTIONS);
    }
    if (svcRole.isRequiredSvc) {
      returnData.push(StepActionTypeEnum.STEP7_REQUIREDSERVICES);
    }
    if (svcRole.isAdditionalJobOption) {
      returnData.push(StepActionTypeEnum.STEP8_ADDITIONNALJOBOPTIONS);
    }
    if (svcRole.isOptionalSvc) {
      returnData.push(StepActionTypeEnum.STEP8_OPTIONALSERVICES);
    }
    return [...returnData, StepActionTypeEnum.STEP9_EXTRA];
  }, [svcRole]);

  const confirmCancel = () => {
    if (sku.readonly) {
      onCancel();
      dispatch.sku.setSingleStep(0);
    } else {
      confirm({
        title: 'Are you sure exit without saving?',
        okText: 'Confirm',
        cancelText: 'Save',
        centered: true,
        onOk() {
          return new Promise(async (resolve, reject) => {
            onCancel();
            dispatch.sku.setSingleStep(0);
            resolve('success');
          });
        },
        onCancel() {
          return new Promise(async (resolve, reject) => {
            await doStepAction();
            onCancel();
            dispatch.sku.setMultiStep(0);
            resolve('success');
          });
        },
      });
    }
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            confirmCancel();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline
              ellipsis={{
                tooltip: topSku ? '[' + topSku.name + ']' : sku?.name,
              }}
            >
              {topSku ? '[' + topSku.name + '] ' + sku.name : sku?.name}
            </Headline>
          }
          subTitle={<RevxUnitSelect />}
          extra={[
            <>
              <RevxButton
                onClick={async () => {
                  setLoading(true);
                  const skuDraft = await skuService.startEditSku(sku);
                  dispatch.sku.setState({ curItem: skuDraft });
                  setLoading(false);
                }}
                brandGreen
                key="refreshTip"
              >
                <Tooltip title={t('platform.sku.wizard.refreshTip')}>
                  {t('common.refresh')}
                </Tooltip>
              </RevxButton>
              <RevxButton onClick={confirmCancel} brandGreen key="cancel">
                {t('common.cancel')}
              </RevxButton>
            </>,
          ]}
        />
      </Affix>
    </div>
  );
  // const [percent, setPercent] = useState(0);

  const percent = useMemo(() => {
    if (singleStep >= totalStep) {
      return 100;
    }
    return Math.ceil((singleStep * 100) / totalStep);
  }, [singleStep, totalStep]);

  const doStepAction = async () => {
    dispatch.sku.setRevxSpinloading(true);
    if (stepAction) {
      if (stepType[singleStep] && stepAction[stepType[singleStep]]) {
        /* default params === sku */
        const payload = stepActionPayload?.[stepType[singleStep]] || sku;
        const res = await stepAction[stepType[singleStep]](payload);
        if (!res) {
          dispatch.sku.setRevxSpinloading(false);
          throw new Error('step error');
        }
      }
    }
    dispatch.sku.setRevxSpinloading(false);
  };

  const isPackagingSku = useMemo(() => {
    return (
      topSku?.skuType?.name === SKU_TYPE_NAME_PACKAGING ||
      sku?.skuType?.name === SKU_TYPE_NAME_PACKAGING
    );
  }, [topSku?.skuType?.name, sku?.skuType?.name]);

  return (
    <RevxSpin spinning={revxSpinloading || loading}>
      <div className="single-sku-config-wizard-wrapper">
        {actionBar}
        <div className="single-sku-config-wizard">
          <RevxFlexLayout align="top">
            <div className="single-sku-config-wizard-left">
              <div className="single-sku-config-wizard-progress-panel">
                <div className="single-sku-config-wizard-title">
                  <span className="brand-green">
                    {t('platform.sku.wizard.configuration')}
                  </span>{' '}
                  {t('platform.sku.wizard.options')}
                </div>

                <div style={{ marginRight: 0 }}>
                  <Progress
                    percent={percent}
                    status={singleStep < totalStep ? 'exception' : 'active'}
                    showInfo={false}
                  />
                </div>
                <Text>
                  {percent}% {t('platform.sku.wizard.complete')}
                </Text>
                <RevxButton
                  brandGreen
                  className="step-btn"
                  onClick={async () => {
                    try {
                      await doStepAction();

                      if (singleStep + 1 >= totalStep) {
                        dispatch.sku.setSingleStep(0);
                        onCancel();
                        return;
                      }
                      dispatch.sku.setSingleStep(singleStep + 1);
                    } catch {}
                  }}
                >
                  {t('platform.sku.wizard.nextStep')}
                </RevxButton>
              </div>
              <Divider type="horizontal" />

              <Steps
                direction="vertical"
                current={singleStep}
                progressDot={(iconDot, { index, status }) => {
                  return status === 'process' ? (
                    <DoneCircleIcon />
                  ) : index < singleStep ? (
                    <DoneCircleIcon />
                  ) : (
                    <UncheckedCircleIcon />
                  );
                }}
                onChange={async (step) => {
                  try {
                    await doStepAction();

                    if (step >= totalStep) {
                      onCancel();
                    } else {
                      dispatch.sku.setSingleStep(step);
                    }
                  } catch {}
                }}
              >
                <Step title={t('platform.sku.form.label.quantities')} />
                <Step
                  title={
                    isPackagingSku
                      ? t('platform.sku.form.label.sizes')
                      : t('platform.sku.form.label.sizesOrientation')
                  }
                  description={`${t('platform.sku.wizard.whichSizes')} ${t(
                    'platform.sku.wizard.willYouSupport'
                  )}`}
                />
                <Step
                  title={t('platform.sku.wizard.title.papers')}
                  description={`${t('platform.sku.wizard.define')} ${t(
                    'platform.sku.wizard.papersTypes'
                  )}`}
                />
                <Step title={t('platform.sku.wizard.title.presses')} />
                <Step title={t('platform.sku.wizard.title.upload')} />
                <Step
                  title={t('platform.sku.wizard.title.inkFinishes')}
                  description={`${t(
                    'platform.sku.wizard.whichColorsAndFinishes'
                  )} ${t('platform.sku.wizard.areSupported')}`}
                />
                {svcRole.isJobOption && (
                  <Step title={t('platform.sku.form.label.jobOptions')} />
                )}
                {svcRole.isRequiredSvc && (
                  <Step title={t('platform.sku.form.label.requiredServices')} />
                )}
                {svcRole.isAdditionalJobOption && (
                  <Step
                    title={t('platform.sku.form.label.additionalJobOptions')}
                  />
                )}
                {svcRole.isOptionalSvc && (
                  <Step title={t('platform.sku.form.label.optionalServices')} />
                )}
                <Step title={t('platform.sku.form.label.extraSpecValues')} />
              </Steps>
            </div>
            <div className="single-sku-config-wizard-right">
              <SingleWizardStep
                step={singleStep}
                sku={sku}
                topSku={topSku}
                skuPart={skuPart}
              />
            </div>
          </RevxFlexLayout>
        </div>
        ,
      </div>
    </RevxSpin>
  );
};

export default withMeasurementUnitScope(SingleSkuConfigWizard);
